/* eslint-disable guard-for-in,no-restricted-syntax */

import { isNullOrEmpty } from "../util/helpers";

/**
 * 解析参数
 */
export function parseQuery(search: string = window.location.search): object {
  if (typeof search !== "string") {
    return {};
  }

  if (search[0] === "?") {
    // eslint-disable-next-line no-param-reassign
    search = search.substring(1);
  }

  if (search === "") {
    return {};
  }

  const params = {};
  const splitSearch = search.split("&");

  splitSearch
    .filter((v) => !!v)
    .forEach((keyVal) => {
      const [key, val = ""] = keyVal.split("=");
      const decodeVal = decodeURIComponent(val);
      if (params[key]) {
        params[key] = [params[key], decodeVal];
      } else {
        params[key] = decodeVal;
      }
    });

  return params;
}

/**
 * 解析路径
 */
export function parsePath(href = window.location.href) {
  const [pathname, strQuery] = href.split("?");
  const query = parseQuery(strQuery);
  return {
    pathname,
    query: query as any,
  };
}

/**
 * 转义url参数
 * @param params
 */
export function stringify(params: object): string {
  if (typeof params !== "object") {
    return "";
  }

  let arrParams: string[] = [];
  for (const key in params) {
    const paramType = typeof params[key];
    if (paramType === "object") {
      if (params[key] instanceof Array) {
        // 数组的话需要依次处理
        arrParams = arrParams.concat(
          params[key].map(
            (val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
          )
        );
      } else if (params[key]) {
        arrParams.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
        );
      }
    } else if (paramType === "boolean") {
      // 布尔值只控制key存在或者不存在
      if (params[key]) {
        arrParams.push(`${encodeURIComponent(key)}`);
      }
    } else if (paramType !== "undefined") {
      arrParams.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
      );
    }
  }

  return arrParams.join("&");
}

/**
 * 合并url与param
 * @param url
 * @param param
 */
export function joinParams(url?: string, param?: object) {
  const strParams = stringify(param || {});
  if (!strParams) {
    return url;
  }

  if (url) {
    const qPos = url.indexOf("?");
    if (qPos > -1) {
      if (qPos === url.length - 1) {
        return `${url}${strParams}`;
      }

      return `${url}&${strParams}`.replace("&&", "&");
    }

    return `${url}?${strParams}`;
  }

  return `?${strParams}`;
}

/**
 * 合并host与path
 * @param host
 * @param path
 */
export function joinHost(host: string, path?: string) {
  if (!path) {
    return host;
  }
  const last_host_char = host[host.length - 1];
  const first_path_char = path[0];
  const combine_str = last_host_char + first_path_char;
  if (combine_str === "//") {
    // 连续俩反斜杠，需要去掉一个
    return `${host}${path.substring(1)}`;
  }

  if (combine_str.indexOf("/") > -1) {
    return `${host}${path}`;
  }

  return `${host}/${path}`;
}

/**
 * 判断一个url是不是包含了host
 * @param url
 */
export function isFullUrl(url: string) {
  return /^(https?:)?\/\//.test(url);
}

const prefixTest = new RegExp(`^/?${process.env.PREFIX}/`);
/**
 * 是不是在同一子项目下
 * @param url
 */
export function isSameSite(url: string) {
  return prefixTest.test(url);
}

/**
 * 对跳转参数进行处理
 * TODO: 当前没有处理url不是string类型的情况，理论上也不会有人这样用哈
 * @param url
 * @param asUrl
 * @param option
 */
export function formatJumpArguments(
  url: string,
  asUrl?: any,
  option = {} as any
): [string, string, any] {
  let temp_url = url;
  let temp_asUrl = asUrl;
  if (
    !asUrl && // 已经指定了asUrl的情况下不需要处理
    !isFullUrl(url) && // 传入了完整路径不需要处理
    isSameSite(url) // 不在同一个子站上不需要处理
  ) {
    temp_url = url.replace(prefixTest, "/");
    temp_asUrl = url;
  }

  if (option.params) {
    temp_url = joinParams(temp_url, option.params) as string;
    if (temp_asUrl) {
      temp_asUrl = joinParams(temp_asUrl, option.params);
    }
  }

  return [temp_url, temp_asUrl, option];
}

export function replaceUrlParams(
  key: string | number,
  value: string | number,
  url?: string
): void;
export function replaceUrlParams(
  params: Record<string, string | number>,
  url?: string
): void;

/** 替换或添加url中的参数 */
export function replaceUrlParams(
  keyOrParams,
  valueOrUrl,
  url = window.location.href
) {
  const urlObject = new URL(url);

  if (typeof keyOrParams === "object") {
    Object.keys(keyOrParams).forEach((key) => {
      const value = keyOrParams[key];
      if (isNullOrEmpty(value)) {
        urlObject.searchParams.delete(key);
      } else {
        urlObject.searchParams.set(key, value);
      }
    });
  } else {
    if (isNullOrEmpty(valueOrUrl)) {
      urlObject.searchParams.delete(keyOrParams);
    } else {
      urlObject.searchParams.set(keyOrParams, valueOrUrl);
    }
  }

  window.history.replaceState(window.history.state, "", urlObject.href);
}

export function clearUrlParams(url = window.location.href) {
  const urlObject = new URL(url);
  window.history.replaceState(
    window.history.state,
    "",
    `${urlObject.origin}${urlObject.pathname}`
  );
}

export function getUrlParams(url = window.location.href) {
  const urlObjet = new URL(url);
  const searchParams = new URLSearchParams(urlObjet.search);

  return Array.from(searchParams.keys()).reduce((params, key) => {
    params[key] = searchParams.get(key);
    return params;
  }, {} as Record<string, string>);
}

export const fixHttp = (url = "") => {
  return url.replace(/^http:/, "https:");
};
